import API_BASE from '../../config';
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import LordIcon from '../LordIcon';
import LORDICONS from '../../lordicons';
import axios from 'axios';

const ownerNav = (t: (key: string) => string) => [
  { name: t('dashboard'),            icon: LORDICONS.dashboard,   path: '/dashboard' },
  { name: t('reports'),              icon: LORDICONS.analytics,   path: '/reports' },
  { name: t('shifts'),               icon: LORDICONS.calendar,    path: '/shifts' },
  { name: t('attendance.requests'),  icon: LORDICONS.bell,        path: '/attendance-requests' },
  { name: t('vacation'),             icon: LORDICONS.calendar,    path: '/vacation' },
  { name: t('announcements'),        icon: LORDICONS.bell,        path: '/announcements' },
  { name: t('messages'),             icon: LORDICONS.chat,        path: '/messages' },
  { name: t('employees'),            icon: LORDICONS.users,       path: '/employees' },
  { name: t('groups.title'),         icon: LORDICONS.users,       path: '/user-groups' },
  { name: t('managers'),             icon: LORDICONS.user,        path: '/managers' },
  { name: t('contracts'),            icon: LORDICONS.document,    path: '/contracts' },
  { name: t('workplaces'),           icon: LORDICONS.building,    path: '/workplaces' },
  { name: t('positions'),            icon: LORDICONS.clipboard,   path: '/positions' },
  { name: t('job.board'),            icon: LORDICONS.briefcase,   path: '/job-board' },
  { name: t('job.applications'),     icon: LORDICONS.list,        path: '/job-applications' },
  { name: t('recent.activity'),      icon: LORDICONS.history,     path: '/recent-activity' },
];

const restrictedOwnerNav = (t: (key: string) => string) => [
  { name: t('dashboard'),      icon: LORDICONS.dashboard, path: '/dashboard' },
  { name: t('reports'),        icon: LORDICONS.analytics, path: '/reports' },
  { name: t('announcements'),  icon: LORDICONS.bell,      path: '/announcements' },
  { name: t('contracts'),      icon: LORDICONS.document,  path: '/contracts' },
  { name: t('employees'),      icon: LORDICONS.users,     path: '/employees' },
  { name: t('managers'),       icon: LORDICONS.user,      path: '/managers' },
];

const employeeNav = (t: (key: string) => string) => [
  { name: t('dashboard'),       icon: LORDICONS.dashboard, path: '/dashboard' },
  { name: t('shifts'),          icon: LORDICONS.calendar,  path: '/shifts' },
  { name: t('announcements'),   icon: LORDICONS.bell,      path: '/announcements' },
  { name: t('recent.activity'), icon: LORDICONS.history,   path: '/recent-activity' },
  { name: t('my.shifts'),       icon: LORDICONS.list,      path: '/my-shifts' },
  { name: t('vacation'),        icon: LORDICONS.calendar,  path: '/vacation' },
  { name: t('my.contracts'),    icon: LORDICONS.clipboard, path: '/my-contracts' },
  { name: t('my.jobs'),         icon: LORDICONS.briefcase, path: '/my-jobs' },
  { name: t('messages'),        icon: LORDICONS.chat,      path: '/messages' },
  { name: t('cv.manager'),      icon: LORDICONS.document,  path: '/cv-manager' },
  { name: t('job.board'),       icon: LORDICONS.briefcase, path: '/job-board' },
];

const managerNav = (t: (key: string) => string) => [
  { name: t('dashboard'),           icon: LORDICONS.dashboard,  path: '/dashboard' },
  { name: t('reports'),             icon: LORDICONS.analytics,  path: '/reports' },
  { name: t('shifts'),              icon: LORDICONS.calendar,   path: '/shifts' },
  { name: t('attendance.requests'), icon: LORDICONS.bell,       path: '/attendance-requests' },
  { name: t('vacation'),            icon: LORDICONS.calendar,   path: '/vacation' },
  { name: t('announcements'),       icon: LORDICONS.bell,       path: '/announcements' },
  { name: t('messages'),            icon: LORDICONS.chat,       path: '/messages' },
  { name: t('employees'),           icon: LORDICONS.users,      path: '/employees' },
  { name: t('groups.title'),        icon: LORDICONS.users,      path: '/user-groups' },
  { name: t('workplaces'),          icon: LORDICONS.building,   path: '/workplaces' },
  { name: t('job.board'),           icon: LORDICONS.briefcase,  path: '/job-board' },
  { name: t('job.applications'),    icon: LORDICONS.clipboard,  path: '/job-applications' },
  { name: t('positions'),           icon: LORDICONS.clipboard,  path: '/positions' },
  { name: t('my.jobs'),             icon: LORDICONS.briefcase,  path: '/my-jobs' },
  { name: t('recent.activity'),     icon: LORDICONS.history,    path: '/recent-activity' },
];

/** Employee/manager with no workplace - still need Messages for job-application chat */
const jobSeekerNav = (t: (key: string) => string) => [
  { name: t('announcements'), icon: LORDICONS.bell,      path: '/announcements' },
  { name: t('messages'),   icon: LORDICONS.chat,      path: '/messages' },
  { name: t('my.jobs'),    icon: LORDICONS.briefcase, path: '/my-jobs' },
  { name: t('cv.manager'), icon: LORDICONS.document,  path: '/cv-manager' },
  { name: t('job.board'),  icon: LORDICONS.briefcase, path: '/job-board' },
];

const superAdminNav = (t: (key: string) => string) => [
  { name: t('admin.nav.dashboard'), icon: LORDICONS.dashboard, path: '/admin' },
];

const Sidebar: React.FC = () => {
  const { t } = useLanguage();
  const [role, setRole] = useState<'OWNER' | 'EMPLOYEE' | 'MANAGER' | 'SUPER_ADMIN'>('OWNER');
  const [unreadCount, setUnreadCount] = useState(0);
  const [announcementsUnread, setAnnouncementsUnread] = useState(0);
  const [hasWorkplace, setHasWorkplace] = useState<boolean | null>(null);
  const [contractsEnabled, setContractsEnabled] = useState<boolean | null>(null);
  const [managersCanManageJobs, setManagersCanManageJobs] = useState<boolean | null>(null);
  const [notificationCounts, setNotificationCounts] = useState<{[key: string]: number}>({});
  const [isRestricted, setIsRestricted] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [sidebarUser, setSidebarUser] = useState<{ firstName?: string; lastName?: string; email?: string; avatarUrl?: string } | null>(null);

  useEffect(() => {
    const loadUser = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setRole(user.role);
          setBusinessId(user.businessId || null);
          setSidebarUser({ firstName: user.firstName, lastName: user.lastName, email: user.email, avatarUrl: user.avatarUrl });
        }
      } catch {}
    };
    loadUser();
    window.addEventListener('storage', loadUser);
    window.addEventListener('userUpdated', loadUser);
    return () => {
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('userUpdated', loadUser);
    };
  }, []);

  useEffect(() => {
    if (!businessId) return;
    const fetchContractsEnabled = () => {
      axios.get(`/api/contracts/businesses/${businessId}/contracts/enabled`, { withCredentials: true })
        .then(res => setContractsEnabled(res.data.contractsEnabled === true))
        .catch(() => setContractsEnabled(false));
    };
    fetchContractsEnabled();
    const handler = () => fetchContractsEnabled();
    window.addEventListener('contractsSettingsChanged', handler);
    return () => window.removeEventListener('contractsSettingsChanged', handler);
  }, [businessId]);

  useEffect(() => {
    if (!businessId || role !== 'MANAGER') {
      setManagersCanManageJobs(null);
      return;
    }
    const fetchManagersJobs = () => {
      axios.get(`/api/contracts/businesses/${businessId}/managers-jobs/enabled`, { withCredentials: true })
        .then(res => setManagersCanManageJobs(res.data.managersCanManageJobs !== false))
        .catch(() => setManagersCanManageJobs(false));
    };
    fetchManagersJobs();
    const handler = () => fetchManagersJobs();
    window.addEventListener('managersJobsSettingsChanged', handler);
    return () => window.removeEventListener('managersJobsSettingsChanged', handler);
  }, [businessId, role]);

  useEffect(() => {
    if (!businessId || role !== 'OWNER') return;
    const fetchRestrictionStatus = async () => {
      try {
        const response = await fetch(API_BASE + '/api/credits/balance', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setIsRestricted(!data.data.isFreeTier && data.data.balance <= 0);
          }
        }
      } catch {}
    };
    fetchRestrictionStatus();
  }, [businessId, role]);

  useEffect(() => {
    if (role !== 'EMPLOYEE' && role !== 'MANAGER') {
      setHasWorkplace(null);
      return;
    }
    const fetchBusinesses = () => {
      fetch(API_BASE + '/api/my-businesses', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setHasWorkplace(Array.isArray(data.businesses) && data.businesses.length > 0);
        })
        .catch(() => setHasWorkplace(false));
    };
    fetchBusinesses();
    const handler = () => fetchBusinesses();
    window.addEventListener('businessMembershipChanged', handler);
    return () => window.removeEventListener('businessMembershipChanged', handler);
  }, [role]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(API_BASE + '/api/messages/unread/counts', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          const totalUnread = Object.values(data).reduce((total: number, item: any) => total + item.count, 0);
          setUnreadCount(totalUnread);
        }
      } catch {}
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAnnouncementsUnread = async () => {
      try {
        const response = await fetch(API_BASE + '/api/notifications/inbox/unread-count?channel=web', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setAnnouncementsUnread(Number(data.count) || 0);
        }
      } catch {}
    };
    fetchAnnouncementsUnread();
    const interval = setInterval(fetchAnnouncementsUnread, 15000);
    const onUpdate = () => fetchAnnouncementsUnread();
    window.addEventListener('announcementsUpdated', onUpdate);
    window.addEventListener('ompli:inbox-updated', onUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('announcementsUpdated', onUpdate);
      window.removeEventListener('ompli:inbox-updated', onUpdate);
    };
  }, []);

  useEffect(() => {
    const handleMessageRead = () => {
      fetch(API_BASE + '/api/messages/unread/counts', { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            const totalUnread = Object.values(data).reduce((total: number, item: any) => total + item.count, 0);
            setUnreadCount(totalUnread);
          }
        })
        .catch(() => {});
    };
    const handleUnreadUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail as { total?: number } | undefined;
      if (detail && typeof detail.total === 'number') {
        setUnreadCount(detail.total);
      } else {
        handleMessageRead();
      }
    };
    window.addEventListener('messagesRead', handleMessageRead);
    window.addEventListener('messagesUnreadUpdated', handleUnreadUpdated);
    return () => {
      window.removeEventListener('messagesRead', handleMessageRead);
      window.removeEventListener('messagesUnreadUpdated', handleUnreadUpdated);
    };
  }, []);

  const refreshNotificationCounts = async () => {
    try {
      const response = await fetch(API_BASE + '/api/notifications/counts', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNotificationCounts(data);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    refreshNotificationCounts();
    const interval = setInterval(refreshNotificationCounts, 30000);
    const onRefresh = () => refreshNotificationCounts();
    window.addEventListener('notificationsUpdated', onRefresh);
    window.addEventListener('messagesRead', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsUpdated', onRefresh);
      window.removeEventListener('messagesRead', onRefresh);
    };
  }, [role]);

  // Mark activity badges as seen when the user opens the related page (messages excluded).
  useEffect(() => {
    const path = location.pathname;
    if (path === '/messages' || path.startsWith('/messages/')) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(API_BASE + '/api/notifications/seen', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path }),
        });
        if (!res.ok || cancelled) return;
        await refreshNotificationCounts();
        window.dispatchEvent(new Event('notificationsUpdated'));
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await fetch(API_BASE + '/api/logout', { method: 'POST', credentials: 'include' });
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getNotificationCount = (itemName: string, itemPath?: string) => {
    if (itemPath === '/announcements') return announcementsUnread;
    // Live AJAX unread for Messages (faster than /api/notifications/counts)
    if (itemPath === '/messages') return unreadCount;
    const notificationMap: {[key: string]: string} = {
      [t('shifts')]: 'shifts',
      [t('attendance.requests')]: 'attendance_requests',
      [t('employees')]: 'employees',
      [t('managers')]: 'managers',
      [t('workplaces')]: 'workplaces',
      [t('contracts')]: 'contracts',
      [t('job.board')]: 'job_board',
      [t('job.applications')]: 'job_applications',
      [t('positions')]: 'positions',
      [t('recent.activity')]: 'recent_activity',
      [t('my.shifts')]: 'my_shifts',
      [t('my.contracts')]: 'my_contracts',
      [t('cv.manager')]: 'cv_manager',
    };
    const notificationKey = notificationMap[itemName];
    return notificationKey ? notificationCounts[notificationKey] || 0 : 0;
  };

  const navItems =
    role === 'SUPER_ADMIN' ? superAdminNav(t) :
    role === 'OWNER' ? (isRestricted ? restrictedOwnerNav(t) : ownerNav(t)) :
    (role === 'MANAGER' || role === 'EMPLOYEE') && hasWorkplace === false ? jobSeekerNav(t) :
    role === 'MANAGER' ? managerNav(t) :
    employeeNav(t);

  const filteredNav = navItems.filter(item => {
    if (contractsEnabled === false && (item.name === t('contracts') || item.name === t('my.contracts'))) {
      return false;
    }
    if (
      role === 'MANAGER' &&
      managersCanManageJobs === false &&
      (item.path === '/job-board' || item.path === '/job-applications')
    ) {
      return false;
    }
    return true;
  });

  return (
    <aside
      data-tour="sidebar"
      className="h-[95vh] w-64 bg-[#FFFFFF] border border-[#E8EDF5] rounded-card shadow-sm m-4 flex flex-col"
    >
      <div className="flex flex-col items-start justify-center py-8 px-6">
        <div className="flex items-center mb-2">
          <img
            src="/images/ompli-logo-blue.png"
            alt="Ompli Logo"
            className="h-7 w-auto"
          />
        </div>
      </div>

      <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
        {filteredNav.map((item) => {
          const notificationCount = getNotificationCount(item.name, item.path);
          const isActive =
            (item.path === '/shifts' &&
              (location.pathname === '/shifts' ||
                location.pathname.startsWith('/shift-presets'))) ||
            location.pathname === item.path ||
            (item.path === '/announcements' && location.pathname.startsWith('/announcements'));

          const tourAttr =
            item.path === '/announcements'
              ? 'nav-announcements'
              : item.path === '/my-shifts'
                ? 'nav-my-shifts'
                : item.path === '/shifts'
                  ? 'nav-shifts'
                  : undefined;

          return (
            <NavLink
              key={item.name}
              to={item.path}
              data-tour={tourAttr}
              className={() =>
                `flex items-center px-4 py-2 rounded-xl transition font-medium text-sm gap-3 relative ${
                  isActive
                    ? 'bg-[#EAF1FF] text-[#0065F4] shadow-sm'
                    : 'text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4]'
                }`
              }
            >
              <LordIcon
                src={item.icon}
                trigger={isActive ? 'loop-on-hover' : 'hover'}
                size={20}
                color={isActive ? '#0065F4' : '#6B7280'}
                hoverColor="#0065F4"
                target="a"
              />
              <span>{item.name}</span>
              {notificationCount > 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-[#E8EDF5] pt-3 pb-4 px-4">
        {/* User identity */}
        {sidebarUser && (
          <NavLink to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F1F5FF] transition mb-1 group">
            {sidebarUser.avatarUrl ? (
              <img
                src={`${API_BASE}${sidebarUser.avatarUrl}`}
                alt="avatar"
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-[#e5e7eb]"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">
                  {(sidebarUser.firstName?.[0] || '') + (sidebarUser.lastName?.[0] || '')}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                {sidebarUser.firstName} {sidebarUser.lastName}
              </div>
              <div className="text-xs text-gray-400 truncate">{sidebarUser.email}</div>
            </div>
          </NavLink>
        )}
        <NavLink
          to="/settings"
          className="flex items-center px-4 py-2 rounded-xl text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4] font-medium text-sm gap-3"
        >
          <LordIcon src={LORDICONS.settings} trigger="hover" size={20} color="#6B7280" hoverColor="#0065F4" target="a" />
          <span>{t('settings')}</span>
        </NavLink>
        <NavLink
          to="/docs"
          data-tour="help"
          className="flex items-center px-4 py-2 rounded-xl text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4] font-medium text-sm gap-3 mt-1"
        >
          <LordIcon src={LORDICONS.info} trigger="hover" size={20} color="#6B7280" hoverColor="#0065F4" target="a" />
          <span>{t('tour.help')}</span>
        </NavLink>
        <a
          href="/login"
          onClick={handleLogout}
          className="flex items-center px-4 py-2 rounded-xl text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4] font-medium text-sm gap-3 mt-1 cursor-pointer"
        >
          <LordIcon src={LORDICONS.logout} trigger="hover" size={20} color="#6B7280" hoverColor="#0065F4" target="a" />
          <span>{t('logout')}</span>
        </a>
      </div>
    </aside>
  );
};

export default Sidebar;
